import React, { useCallback, useReducer, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useMutation } from '@apollo/client'

import { BackContainer } from 'components/Common/BackContainer'
import BodyContainerWithHeader from 'components/Common/BodyContainerWithHeader'

import { DUPLICATE_KEY_ERROR } from 'constants/error'
import { CAR_SETTINGS_SUB_ROUTES } from 'constants/routes'
import { textFiles } from 'constants/textFiles'
import useNotification from 'hooks/useNotification'
import useTranslation from 'hooks/useTranslation'
import { CountryVersion } from 'models/car'
import {
  CountryVersionCreationAction,
  CountryVersionCreationModel,
} from 'models/countryVersionCreation'
import {
  BaseIdEntity,
  GenericData,
  GenericInputVariable,
} from 'models/services/base'
import { CreateCarSettingGenericInputType } from 'models/services/CarSettings/creation'
import { PublicationStatus } from 'models/status'
import countryVersionCreationReducer from 'reducers/countryVersionCreationReducer'
import {
  createCountryVersionStepperItems,
  INITIAL_COUNTRY_VERSION_CREATION_STEPS as steps,
} from 'utils/CarSettings/CountryVersion/creation'
import { validateGraphQLErrorCode } from 'utils/error'
import { getTranslationsFilteredByLanguage } from 'utils/translation'

import { CREATE_COUNTRY_VERSION } from 'graphQL/Operations/CountryVersion/Creation/mutations'

import { StyledBox } from 'styles/operation/creation'

const initialCountryVersionInformationState: CountryVersion = {
  country: '',
  status: PublicationStatus.PUBLISHED,
  translations: [],
}

const initialData: CountryVersionCreationModel = {
  countryVersionInformation: initialCountryVersionInformationState,
}

const CountryVersionCreationPage = () => {
  const [currentStep, setCurrentStep] = useState<number>(0)

  const [countryVersionData, dispatch] = useReducer(
    countryVersionCreationReducer,
    initialData
  )

  const { countryVersionInformation } = countryVersionData
  const { translations } = countryVersionInformation

  const history = useHistory()
  const { show } = useNotification()

  const { text } = useTranslation(textFiles.COUNTRY_VERSION_CREATION)
  const { text: generalText } = useTranslation(textFiles.GENERAL)
  const { stepper } = text

  const stepperItems = createCountryVersionStepperItems(stepper)

  const updateCountryVersionData = (action: CountryVersionCreationAction) => {
    dispatch(action)
  }

  const handleBack = () => {
    if (currentStep - 1 >= 0) setCurrentStep((step) => step - 1)
  }
  const handleContinue = () => {
    setCurrentStep((step) => step + 1)
  }

  const [createCountryVersion, { loading: submitLoading }] = useMutation<
    GenericData<BaseIdEntity>,
    GenericInputVariable<CreateCarSettingGenericInputType>
  >(CREATE_COUNTRY_VERSION, {
    onCompleted() {
      show({
        updatedSeverity: 'success',
      })
      history.push(CAR_SETTINGS_SUB_ROUTES.COUNTRY_VERSION_LISTING)
    },
    onError(error) {
      const { errorExists } = validateGraphQLErrorCode(
        error,
        DUPLICATE_KEY_ERROR
      )
      if (errorExists) {
        show({
          updatedSeverity: 'error',
          message: generalText.notificationText.duplicateName,
        })
      } else
        show({
          updatedSeverity: 'error',
        })
    },
  })

  const handleSubmit = useCallback(() => {
    const { valueEn, valueEs } = getTranslationsFilteredByLanguage(translations)

    createCountryVersion({
      variables: {
        input: {
          name: countryVersionInformation.country,
          name_translations: {
            en: valueEn ? valueEn.description : null,
            es: valueEs ? valueEs.description : null,
          },
          status: countryVersionInformation.status
            ? PublicationStatus.PUBLISHED
            : PublicationStatus.UNPUBLISHED,
        },
      },
    })
  }, [countryVersionInformation, createCountryVersion, translations])

  return (
    <StyledBox>
      <BackContainer />
      <BodyContainerWithHeader
        title={text.title}
        subtitle={text.processDescription}
        stepperItems={stepperItems}
        currentStep={currentStep}
      >
        {React.createElement(steps[currentStep], {
          handleContinue,
          handleBack,
          handleSubmit,
          submitLoading,
          countryVersionData,
          updateCountryVersionData,
        })}
      </BodyContainerWithHeader>
    </StyledBox>
  )
}

export default CountryVersionCreationPage
